import { useCallback, useEffect, useId, useMemo, useRef, useState, type CSSProperties, type SelectHTMLAttributes, type KeyboardEvent } from "react";
import { createPortal } from "react-dom";
import { filterSelectOptions, nextSelectValue, selectOptions, selectPopupPosition, type SelectOption } from "../selectModel";
import { SelectPopup, selectOptionId } from "./SelectPopup";
import { beginWorkerPick } from "../workerPick";
import { useDesignSystem } from "./ui/DesignSystem";
import "./Select.css";

/** The hidden select is a form bridge; visible selection is always custom. */
export function Select({ children, className, style, popupClassName, searchable: searchableProp, pickWorkers, ...props }: SelectHTMLAttributes<HTMLSelectElement> & { popupClassName?: string; searchable?: boolean;
  /** 열려 있는 동안 판의 작업자 이름을 눌러서도 고를 수 있게 한다. */
  pickWorkers?: boolean }) {
  const themed = useDesignSystem();
  const native = useRef<HTMLSelectElement>(null), trigger = useRef<HTMLButtonElement>(null), popup = useRef<HTMLDivElement>(null), search = useRef<HTMLInputElement>(null);
  const composing = useRef(false), frame = useRef(0);
  const id = useId(), options = useMemo(() => selectOptions(children), [children]);
  const [internalValue, setInternalValue] = useState(() => String(props.defaultValue ?? options[0]?.value ?? ""));
  const value = String(props.value ?? internalValue);
  const [rail, setRail] = useState(false), [open, setOpen] = useState(false), [query, setQuery] = useState(""), [activeValue, setActiveValue] = useState<string | null>(null);
  const [searchable, setSearchable] = useState(false);
  const [position, setPosition] = useState<CSSProperties>({});
  const [description, setDescription] = useState(props["aria-label"] ?? "항목 선택");
  const knownNames = useRef(new Map(options.map(option => [option.value, option.label])));
  const selected = options.find(option => option.value === value);
  /* 빈 값 항목은 고를 수 있는 것이 아니라 "아직 안 골랐다"는 자리표시다. 목록에 세우면
     "작업자 선택"이라는 이름의 작업자가 한 명 있는 것처럼 보이고, 심지어 체크까지 선다.
     닫힌 칸의 글씨로만 쓰고 목록에서는 뺀다 — 네이티브 select에는 그대로 남는다. */
  const visible = useMemo(() => filterSelectOptions(options, query).filter(option => option.value !== ""), [options, query]);
  const optionLayoutKey = open ? JSON.stringify(visible) : "";
  const active = visible.find(option => option.value === activeValue && !option.disabled);
  const lostActive = activeValue !== null && !active;
  const missing = !selected && value !== "";
  const label = selected?.label ?? (missing ? `${knownNames.current.get(value) ?? "이전 선택"} · 항목 없음` : "선택해 주세요");
  const bridgeValue = selected ? value : "";
  useEffect(() => {
    for (const option of options) knownNames.current.set(option.value, option.label);
    const label = native.current?.labels?.[0]?.cloneNode(true) as HTMLElement | undefined;
    label?.querySelectorAll(".custom-select").forEach(node => node.remove());
    const referenced = props["aria-labelledby"]?.split(/\s+/).map(id => document.getElementById(id)?.textContent ?? "").join(" ").trim();
    setDescription(props["aria-label"] ?? (referenced || label?.textContent?.trim() || "항목 선택"));
  }, [props["aria-label"], props["aria-labelledby"], options]);
  useEffect(() => { native.current?.setCustomValidity(missing ? "선택한 항목이 더 이상 존재하지 않습니다. 다시 선택해 주세요." : ""); }, [missing]);

  const place = useCallback(() => {
    if (!trigger.current) return;
    const viewport = window.visualViewport;
    const next = selectPopupPosition(trigger.current.getBoundingClientRect(), {
      left: viewport?.offsetLeft ?? 0, top: viewport?.offsetTop ?? 0,
      width: viewport?.width ?? innerWidth, height: viewport?.height ?? innerHeight, layoutHeight: innerHeight,
    }, themed, document.documentElement.classList.contains("settings-open") ? 0 : 160);
    next.fontSize = getComputedStyle(trigger.current).fontSize;
    setPosition(previous => Object.keys(next).every(key => previous[key as keyof CSSProperties] === next[key as keyof CSSProperties]) ? previous : next);
  }, [themed]);
  const close = (restore = true) => { setOpen(false); composing.current = false; if (restore && trigger.current?.isConnected && !trigger.current.disabled) trigger.current.focus({ preventScroll: true }); };
  const show = (key?: string) => {
    if (props.disabled) return;
    place(); setQuery(""); setSearchable(searchableProp ?? options.length > 6); composing.current = false;
    setActiveValue(key === "Home" || key === "End" ? nextSelectValue(options, null, key) : selected && !selected.disabled ? selected.value : nextSelectValue(options, null, key ?? "Home"));
    setRail(!!trigger.current?.closest(".planner-aside"));
    setOpen(true);
  };
  const choose = (option: SelectOption) => {
    const latest = options.find(item => item.value === option.value);
    if (!latest || latest.disabled || props.disabled || !native.current) return;
    if (props.value === undefined) setInternalValue(latest.value);
    native.current.value = latest.value;
    native.current.dispatchEvent(new Event("change", { bubbles: true }));
    close();
  };
  useEffect(() => {
    const node = native.current, form = node?.form;
    if (!node || !form) return;
    const reset = (event: Event) => queueMicrotask(() => {
      if (event.defaultPrevented || !node.isConnected) return;
      const next = String(props.value ?? props.defaultValue ?? options[0]?.value ?? "");
      if (props.value === undefined) setInternalValue(next);
      node.value = options.some(option => option.value === next) ? next : "";
      setOpen(false); setQuery("");
    });
    form.addEventListener("reset", reset);
    return () => form.removeEventListener("reset", reset);
  }, [props.value, props.defaultValue, options]);

  useEffect(() => {
    if (!open) return;
    const reposition = (event?: Event) => {
      if (event?.target instanceof Node && popup.current?.contains(event.target)) return;
      cancelAnimationFrame(frame.current); frame.current = requestAnimationFrame(place);
    };
    const outside = (event: PointerEvent) => {
      const node = event.target as Node;
      if (popup.current?.contains(node) || trigger.current?.contains(node)) return;
      /* 판의 이름 열은 열린 고르개의 연장이다. 여기서 닫으면 누르는 순간 목록이 사라져
         고를 대상이 없어진다 — 고르는 일은 그 줄의 onClick이 맡는다. */
      if (pickWorkers && node instanceof Element && node.closest("[data-worker-header-id]")) return;
      close(false);
    };
    const observer = new ResizeObserver(() => reposition());
    if (trigger.current) observer.observe(trigger.current);
    document.addEventListener("pointerdown", outside);
    document.addEventListener("scroll", reposition, true); window.addEventListener("resize", reposition);
    window.visualViewport?.addEventListener("resize", reposition); window.visualViewport?.addEventListener("scroll", reposition);
    return () => {
      observer.disconnect(); cancelAnimationFrame(frame.current);
      document.removeEventListener("pointerdown", outside);
      document.removeEventListener("scroll", reposition, true); window.removeEventListener("resize", reposition);
      window.visualViewport?.removeEventListener("resize", reposition); window.visualViewport?.removeEventListener("scroll", reposition);
    };
  }, [open, place]);
  useEffect(() => {
    if (!open || !pickWorkers) return;
    return beginWorkerPick({ options, choose, narrow: value => { setSearchable(true); setQuery(value); } });
  }, [open, pickWorkers, options]);
  useEffect(() => { if (props.disabled) close(false); }, [props.disabled]);
  useEffect(() => {
    if (!open || !active) return;
    const node = document.getElementById(selectOptionId(id, active.value)), list = popup.current?.querySelector(".custom-select-options");
    if (!node || !list) return;
    const row = node.getBoundingClientRect(), bounds = list.getBoundingClientRect();
    if (row.top < bounds.top) list.scrollTop += row.top - bounds.top;
    else if (row.bottom > bounds.bottom) list.scrollTop += row.bottom - bounds.bottom;
  }, [open, active?.value, optionLayoutKey, position]);

  const keydown = (event: KeyboardEvent) => {
    if (composing.current || event.nativeEvent.isComposing || event.nativeEvent.keyCode === 229) { event.stopPropagation(); return; }
    if (event.key === "Escape" && open) { event.preventDefault(); event.stopPropagation(); close(); return; }
    if (event.key === "Tab" && open) {
      const origin = trigger.current!;
      const dialog = origin.closest("dialog[open]");
      const scope = dialog ?? document;
      const focusable = [...scope.querySelectorAll<HTMLElement>("button, input, textarea, select, a[href], [tabindex]")].filter(item => item.tabIndex >= 0 && !item.matches(":disabled") && !item.closest("[inert]") && !popup.current?.contains(item) && item.getClientRects().length && getComputedStyle(item).visibility !== "hidden");
      const index = focusable.indexOf(origin), offset = event.shiftKey ? -1 : 1;
      const target = focusable[index + offset] ?? (dialog ? event.shiftKey ? focusable.at(-1) : focusable[0] : null);
      close(false);
      if (target) { event.preventDefault(); event.stopPropagation(); target.focus(); } else origin.focus();
      return;
    }
    if (["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) {
      event.preventDefault(); if (!open) show(event.key); else setActiveValue(nextSelectValue(visible, active?.value ?? null, event.key));
      return;
    }
    if (event.key === "Enter" && (event.target === trigger.current || event.target === search.current) || event.key === " " && event.target === trigger.current) {
      event.preventDefault(); if (!open) show(); else if (active) choose(active);
    }
  };
  const editQuery = (next: string) => { setQuery(next); setActiveValue(nextSelectValue(filterSelectOptions(options, next), null, "Home")); };
  return <span className={`custom-select ${className ?? ""}`.trim()} style={style}>
    <select {...props} autoFocus={false} defaultValue={undefined} value={bridgeValue} ref={native} tabIndex={-1} aria-hidden="true" className="custom-select-native"
      onChange={event => { if (props.value === undefined) setInternalValue(event.target.value); props.onChange?.(event); }}
      onFocus={event => { props.onFocus?.(event); trigger.current?.focus(); }}
      onInvalid={event => { props.onInvalid?.(event); event.preventDefault(); trigger.current?.focus(); show(); }}>
      {!options.some(option => option.value === "") && !selected && <option value="" hidden disabled />}
      {children}
    </select>
    <button ref={trigger} type="button" role="combobox" className={`custom-select-trigger${missing ? " is-missing" : ""}`} disabled={props.disabled} autoFocus={props.autoFocus}
      aria-expanded={open} aria-controls={open ? `${id}-list` : undefined} aria-haspopup="listbox" aria-label={`${description} 선택창${selected?.attention ? ", 확인 필요" : ""}`} title={label}
      aria-describedby={[`${id}-description`, props["aria-describedby"]].filter(Boolean).join(" ")}
      aria-required={props.required || undefined} aria-invalid={props["aria-invalid"] || missing || undefined}
      aria-activedescendant={open && active ? selectOptionId(id, active.value) : undefined}
      onClick={() => open ? close() : show()} onKeyDown={keydown}>
      <span className="select-value">{label}</span>{selected?.attention && <span className="select-attention-dot" aria-hidden="true" />}<svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true"><path d="m4 6 4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.6" /></svg>
    </button>
    <span id={`${id}-description`} className="custom-select-description">{description}</span>
    {open && createPortal(<SelectPopup id={id} rail={rail} className={[popupClassName, rail ? "custom-select-popup--rail" : ""].filter(Boolean).join(" ") || undefined} popupRef={popup} searchRef={search} position={position} description={description} searchable={searchable}
      query={query} options={visible} value={value} activeValue={active?.value ?? null} lostActive={lostActive} onQuery={editQuery}
      onActive={setActiveValue} onChoose={choose} onKeyDown={keydown} onComposing={value => { composing.current = value; }} />, trigger.current?.closest("dialog") ?? document.body)}
  </span>;
}
